import { Banner, Divider, Label, Select, Switch, Typography } from '@neo4j-ndl/react';
import type { CURRENCY, InstanceSize, Project } from '@nx/state';
import { PROJECT_BILLING_METHOD, TIER } from '@nx/state';
import { isNotNullish, isNullish } from '@nx/stdlib';
import Big from 'big.js';
import { useCallback, useMemo } from 'react';
import { components } from 'react-select';

import { formatCost, getCurrencyForBillingMethod } from '../../utils';
import { gbStringToInt, isSizeAvailableForFormData } from '../entities/helpers';
import type { InstanceSizeFormData } from '../entities/model';
import { BaseInstanceCreateSection } from './base-section';

const formatSize = (
  { value, currency, showPrice }: { value: InstanceSize; key: string; showPrice?: boolean; currency: CURRENCY },
  { context }: { context: string },
) => {
  const cost = formatCost(value.costPerHour, currency);
  if (context === 'value') {
    return (
      <div className="flex w-full flex-row items-center justify-between gap-2 whitespace-break-spaces">
        <div className="flex flex-row items-center gap-2">
          {value.memory} Memory
          <Divider className="border-neutral-border-weak" orientation="vertical" />
          {value.cpu} CPU
        </div>
        {showPrice === true ? (
          <div className="text-neutral-text-weak">
            {value.isTrial ? (
              <Typography variant="subheading-small">
                <Label color="info" fill="outlined" className="normal-case">
                  14 days free trial
                </Label>
              </Typography>
            ) : (
              <>{value.isPrepaidOnly ? 'Prepaid' : <>{cost} /hour</>}</>
            )}
          </div>
        ) : null}
      </div>
    );
  } else if (context === 'menu') {
    return (
      <div className="flex w-full flex-row items-center justify-between gap-2 whitespace-break-spaces">
        <div className="flex flex-row items-center gap-2">
          {value.memory} Memory
          <Divider className="border-neutral-border-weak" orientation="vertical" />
          {value.cpu} CPU
        </div>
        {showPrice === true ? (
          <div className="text-neutral-text-weak">
            {value.isTrial ? (
              <Typography variant="subheading-small">
                <Label color="info" fill="outlined" className="normal-case">
                  14 days free trial
                </Label>
              </Typography>
            ) : (
              <>{value.isPrepaidOnly ? 'Prepaid' : <>{cost} /hour</>}</>
            )}
          </div>
        ) : null}
      </div>
    );
  }
  return null;
};

interface InstanceSizeOption {
  size: InstanceSize;
  title: string;
  isDisabled: boolean;
}

type CompressedInstanceSize = Pick<InstanceSize, 'memory' | 'cpu' | 'isPrepaidOnly'> & {
  // A compressed size should not be a mix of trial and non trial sizes
  isTrial: boolean;
  // Is true if all options are disabled
  isDisabled: boolean;
  instanceSizeOptions: InstanceSizeOption[];
};

const getPriceDifference = (
  compressedSize: CompressedInstanceSize,
  size: InstanceSize,
  projectBillingMethod: PROJECT_BILLING_METHOD,
) => {
  const options = compressedSize.instanceSizeOptions.sort(
    (a, b) => gbStringToInt(a.size.storage) - gbStringToInt(b.size.storage),
  );

  const index = options.findIndex((o) => size.sizeId === o.size.sizeId);
  if (index === -1 || options.length === 1) {
    return '';
  }

  if (index === 0) {
    return '(included)';
  }

  const [baseOption] = options;
  if (isNullish(baseOption)) {
    throw new Error('No base option');
  }
  const diff = Big(size.costPerHour).sub(Big(baseOption.size.costPerHour));
  return `+${formatCost(diff.toString(), getCurrencyForBillingMethod(projectBillingMethod), { textOnly: true })}/hour`;
};

const getDefaultInstanceSizeOption = (
  compressedSize: CompressedInstanceSize,
  value?: InstanceSize,
): InstanceSizeOption => {
  const sortedOptions = compressedSize.instanceSizeOptions;
  sortedOptions.sort((a, b) => gbStringToInt(a.size.storage) - gbStringToInt(b.size.storage));
  const enabledOptions = sortedOptions.filter((is) => !is.isDisabled);

  // Use current value if its one of the enabled options
  for (const option of enabledOptions) {
    if (option.size.sizeId === value?.sizeId) {
      return option;
    }
  }

  // Use the default 1:2 ratio if its one of the enabled options
  for (const option of enabledOptions) {
    if (gbStringToInt(compressedSize.memory) * 2 === gbStringToInt(option.size.storage)) {
      return option;
    }
  }

  // Use the first enabled option
  if (enabledOptions.length > 0) {
    const [enabledOption] = enabledOptions;
    if (isNullish(enabledOption)) {
      throw new Error('Enabled size is undefined');
    }
    return enabledOption;
  }

  // Use the first of the sorted options
  if (sortedOptions.length === 0) {
    throw new Error('No storage sizes');
  }

  const [firstOption] = sortedOptions;
  if (isNullish(firstOption)) {
    throw new Error('Storage size undefined');
  }

  return firstOption;
};

type StorageSelectProps = {
  projectBillingMethod: PROJECT_BILLING_METHOD;
  compressedSize: CompressedInstanceSize;
  size?: InstanceSize;
  disabled: boolean;
  onChange: (size: InstanceSizeOption) => void;
  hidePricing: boolean;
};

const StorageSelect = ({
  projectBillingMethod,
  compressedSize,
  size,
  disabled,
  onChange,
  hidePricing,
}: StorageSelectProps) => {
  const rowOptions = compressedSize.instanceSizeOptions.map((option) => ({
    label: option.size.storage,
    value: option,
    isDisabled: option.isDisabled,
    title: option.title,
  }));

  const selectedRowOption = rowOptions.find((o) => o.value.size.storage === size?.storage);
  const showStorageSelect = compressedSize.instanceSizeOptions.length > 1;

  if (!showStorageSelect && isNotNullish(selectedRowOption)) {
    return (
      <Typography variant="body-medium" className="flex flex-col gap-1">
        <label className="text-neutral-text-weak" htmlFor="storage">
          Storage
        </label>
        <div
          data-testid={`storage-${gbStringToInt(selectedRowOption.value.size.memory)}-${gbStringToInt(selectedRowOption.value.size.storage)}`}
          id="storage"
          className="my-auto px-3 py-2"
        >
          {selectedRowOption.value.size.storage}
        </div>
      </Typography>
    );
  }

  return (
    <Select<{ label: string; value: InstanceSizeOption; isDisabled: boolean; title?: string }>
      type="select"
      size="medium"
      isFluid
      label="Storage"
      htmlAttributes={{ 'data-testid': 'storage-dropdown' }}
      selectProps={{
        // This placeholder only shows when disabled cause we set the value to null if all sizes
        // are not available
        placeholder: 'No valid sizes',
        menuPosition: 'fixed',
        options: rowOptions,
        components: {
          // Override the component to add a title to the disabled options
          Option: (optionProps) => (
            <components.Option
              {...optionProps}
              className={`ndl-select-option ${optionProps.className} min-w-48 justify-between p-1`}
              innerProps={{
                ...optionProps.innerProps,
                title: optionProps.data.title,
              }}
            >
              <Typography
                variant="body-medium"
                className={optionProps.data.isDisabled ? 'text-neutral-text-weakest' : ''}
              >
                {optionProps.data.label}
              </Typography>
              <Typography
                variant="body-medium"
                className={optionProps.data.isDisabled ? 'text-neutral-text-weakest' : 'text-neutral-text-weak'}
              >
                {isNotNullish(compressedSize) &&
                  !hidePricing &&
                  getPriceDifference(compressedSize, optionProps.data.value.size, projectBillingMethod)}
              </Typography>
            </components.Option>
          ),
          SingleValue: (valueProps) => (
            <components.SingleValue
              {...valueProps}
              className={`ndl-select-single-value ${valueProps.className} min-w-48 justify-between p-1`}
              innerProps={{
                ...valueProps.innerProps,
                title: valueProps.data.title,
              }}
            >
              <Typography
                variant="body-medium"
                className={valueProps.data.isDisabled ? 'text-neutral-text-weakest' : ''}
              >
                {valueProps.data.label}
              </Typography>
              <Typography
                variant="body-medium"
                className={valueProps.data.isDisabled ? 'text-neutral-text-weakest' : 'text-neutral-text-weak'}
              >
                {isNotNullish(compressedSize) &&
                  !hidePricing &&
                  getPriceDifference(compressedSize, valueProps.data.value.size, projectBillingMethod)}
              </Typography>
            </components.SingleValue>
          ),
        },
        value: disabled ? null : selectedRowOption,
        onChange: (option) => {
          if (!isNullish(option)) {
            onChange(option.value);
          }
        },
      }}
      isDisabled={rowOptions.length === 1}
      hasBorder={rowOptions.length > 1}
    />
  );
};

const getInstanceSizeOption = (
  compressSize: CompressedInstanceSize,
  showPrice: boolean,
  billingMethod: PROJECT_BILLING_METHOD,
) => {
  const [size] = compressSize.instanceSizeOptions;
  if (isNullish(size)) {
    throw new Error('something');
  }
  return {
    value: size.size,
    key: `memory-${compressSize.memory}-cpu-${compressSize.cpu}`,
    showPrice,
    currency: getCurrencyForBillingMethod(billingMethod),
  };
};

type Props = {
  project: Project;
  data: InstanceSizeFormData;
  options: InstanceSize[];
  hidePricing?: boolean;
  errorMessage?: string;
  onDataChange: (value: InstanceSize) => void;
  initialSize?: InstanceSize;
  resizeThreshold?: number;
  tier?: TIER;
  onShowTrialSizes?: (value: boolean) => void;
  showTrialSizes?: boolean;
};
export const SizeSection = ({
  initialSize,
  resizeThreshold,
  data,
  options,
  hidePricing = false,
  onDataChange,
  project,
  showTrialSizes,
  onShowTrialSizes,
}: Props) => {
  const sizeAvailabilityOptions = useMemo(() => ({ initialSize, resizeThreshold }), [initialSize, resizeThreshold]);
  const isSizeAvailable = useCallback(
    (size: InstanceSize) => {
      return isSizeAvailableForFormData({ ...data, size }, project, options, sizeAvailabilityOptions);
    },
    [sizeAvailabilityOptions, project, options, data],
  );

  const buildContactUrl = () => {
    const contactUrlTierMap = {
      free: 'free',
      mte: 'business-critical',
      professional: 'pro',
      gds: 'gds',
      enterprise: 'vdc',
      dsenterprise: 'dsenterprise',
    };
    let params = '';

    if (isNotNullish(data.tier) && isNotNullish(data.size)) {
      params = `?about_me=${contactUrlTierMap[data.tier]}-${data.size.cloudProvider}-${data.size.memory}`;
    }

    return `https://neo4j.com/contact-us/${params}`;
  };

  const compressedSizes = useMemo(
    () =>
      Object.values(
        options.reduce<Record<string, CompressedInstanceSize>>((prev, instanceSize) => {
          if (instanceSize.memory in prev) {
            const existingItem = prev[instanceSize.memory];
            if (isNotNullish(existingItem)) {
              if (existingItem.isTrial !== instanceSize.isTrial) {
                // Trial sizes need to be kept separate from trial sizes
                // in the compressed size object
                throw new Error('A mixture of trial and non trial sizes was found');
              }

              const newOption: InstanceSizeOption = {
                size: instanceSize,
                isDisabled: !isSizeAvailable(instanceSize).isValid,
                title: '',
              };

              const updatedItem: CompressedInstanceSize = {
                ...existingItem,
                isDisabled: !newOption.isDisabled ? false : existingItem.isDisabled,
                instanceSizeOptions: [...existingItem.instanceSizeOptions, newOption],
              };
              return {
                ...prev,
                [instanceSize.memory]: updatedItem,
              };
            }
          }

          const newOption: InstanceSizeOption = {
            size: instanceSize,
            isDisabled: !isSizeAvailable(instanceSize).isValid,
            title: '',
          };

          const newItem: CompressedInstanceSize = {
            isPrepaidOnly: instanceSize.isPrepaidOnly,
            memory: instanceSize.memory,
            cpu: instanceSize.cpu,
            isTrial: instanceSize.isTrial,
            isDisabled: newOption.isDisabled,
            instanceSizeOptions: [newOption],
          };
          return {
            ...prev,
            [instanceSize.memory]: newItem,
          };
        }, {}),
      ),
    [options, isSizeAvailable],
  );

  const handleChange = (value?: InstanceSize) => {
    if (isNotNullish(value)) {
      if (data.size?.memory !== value.memory) {
        const compSize = compressedSizes.find((size) => size.memory === value.memory);

        if (isNotNullish(compSize)) {
          const alt = getDefaultInstanceSizeOption(compSize, data.size);
          onDataChange(alt.size);
          return;
        }
      }
      onDataChange(value);
    }
  };

  const sizeOptions = useMemo(() => {
    return compressedSizes.map((size) => getInstanceSizeOption(size, !hidePricing, project.billingMethod));
  }, [compressedSizes, hidePricing, project.billingMethod]);

  const selectedSize = useMemo(() => {
    return sizeOptions.find((size) => size.value.memory === data.size?.memory) ?? null;
  }, [data.size?.memory, sizeOptions]);

  const selectedCompressedSize = useMemo(() => {
    return compressedSizes.find((size) => size.memory === data.size?.memory) ?? null;
  }, [compressedSizes, data.size?.memory]);

  return (
    <BaseInstanceCreateSection
      title={
        <div className="flex flex-row items-center justify-between gap-2">
          Sizing
          {project.availableActions.create_pro_trial.enabled &&
            data.tier === TIER.PROFESSIONAL &&
            isNotNullish(onShowTrialSizes) && (
              <Switch
                onChange={(event) => onShowTrialSizes(event.target.checked)}
                label="Show trial offers"
                isChecked={showTrialSizes}
                hasLabelBefore
              />
            )}
        </div>
      }
    >
      <div className="@sm:grid-cols-2 grid grid-cols-1 gap-4">
        <Select
          label="Memory & CPU"
          type="select"
          htmlAttributes={{ 'data-testid': 'memory-dropdown' }}
          selectProps={{
            value: selectedSize,
            options: sizeOptions,
            formatOptionLabel: formatSize,
            onChange: (option) => handleChange(option?.value),
          }}
          size="medium"
          isFluid
        />
        {isNotNullish(selectedCompressedSize) && (
          <StorageSelect
            compressedSize={selectedCompressedSize}
            onChange={(value) => handleChange(value.size)}
            disabled={isNullish(selectedCompressedSize)}
            size={data.size}
            projectBillingMethod={project.billingMethod}
            hidePricing={hidePricing}
          />
        )}
      </div>
      {selectedSize?.value.isPrepaidOnly === true && project.billingMethod !== PROJECT_BILLING_METHOD.PREPAID && (
        <Banner
          hasIcon
          type="warning"
          title="Prepaid billing required"
          description="Large database sizing options are only available through prepaid credits. Please contact our sales team to proceed."
          actions={[
            {
              label: 'Contact sales',
              href: buildContactUrl(),
              fill: 'outlined',
              target: '_blank',
            },
          ]}
        />
      )}
    </BaseInstanceCreateSection>
  );
};
